import { Autocomplete, TextField } from "@mui/material";
import type { Region } from "@przezmorze/api-types";
import type { FC } from "react";
import { useRegions } from "../../admin/dictionaries/useRegions";
import { usePackages } from "../../admin/packages/usePackages";
import type { Currency } from "../../data/BaseEntity";

export interface Package {
  readonly id: string;
  readonly name: string;
  readonly tags: { readonly category: string; readonly value: string }[];
  readonly currency: Currency;
  readonly margin: number;
  readonly days: {
    readonly dayNumber: number;
    readonly details: { readonly description?: string; readonly plan?: string };
    readonly products: {
      readonly id: string;
      readonly optional?: boolean;
      readonly options: string[];
    }[];
  }[];
}

interface Props {
  readonly packageId: string | undefined;
  readonly regionId: string | undefined;
  readonly fullWidth?: boolean;
  readonly size?: "small" | "medium";
  readonly disabled?: boolean;
  readonly disableClearable?: boolean;
  readonly onChange: (p: Region | undefined) => void;
  readonly label?: string;
  readonly placeholder?: string;
}

const RegionSelect: FC<Props> = ({
  packageId,
  regionId,
  fullWidth,
  size,
  disabled,
  onChange,
  disableClearable,
  label = "Region",
}) => {
  const { packages, isLoading: arePackagesLoading } = usePackages();
  const { regions, isLoading: areRegionsLoading } = useRegions();
  const isLoading = arePackagesLoading || areRegionsLoading;
  const selectedPackage = (packages ?? []).find((p) => p.id.id == packageId);

  const selectedRegionId = selectedPackage?.regionId ?? regionId ?? null;

  const options = (regions ?? []).map((p) => ({ id: p.id.id, label: p.name }));

  const selectedOption = options.find((r) => r.id === selectedRegionId) ?? null;

  const handleChange = (selectedId: string | null) => {
    if (selectedId == null) {
      onChange(undefined);
      return;
    }
    const selectedRegion = regions.find((p) => p.id.id === selectedId);
    onChange(selectedRegion);
  };

  return (
    <Autocomplete
      loading={isLoading}
      disablePortal
      disableClearable={disableClearable}
      fullWidth={fullWidth}
      multiple={false}
      size={size}
      disabled={disabled}
      value={selectedOption}
      options={options}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
      onChange={(e, selected) => {
        if (selected == null) {
          handleChange(null);
          return;
        }
        const id = typeof selected === "string" ? selected : selected.id;
        handleChange(id);
      }}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
};

export default RegionSelect;
